import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  ArrowLeft,
  Plus,
  Search,
  MoreVertical,
  Edit,
  Copy,
  Trash2,
  Eye,
  FileText,
  CheckCircle2,
  Loader2,
  ClipboardList,
  UserCheck,
  Shield,
  Leaf,
  FileCheck,
  Settings,
  BarChart3,
  Building2,
  Lock,
  Briefcase,
  RefreshCw,
} from 'lucide-react';
import { onboardingAPI, OnboardingDataset } from '../../lib/api';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { getApiErrorMessage } from '../../utils/errorUtils';

const datasetTypeLabels: Record<string, string> = {
  onboarding: 'Onboarding',
  compliance: 'Compliance',
  risk_assessment: 'Risk Assessment',
  esg: 'ESG',
  financial: 'Financial',
  quality: 'Quality',
  security: 'Security',
  capability: 'Capability',
  periodic_review: 'Periodic Review',
  code_of_conduct: 'Code of Conduct',
  custom: 'Custom',
};

const datasetTypeColors: Record<string, string> = {
  onboarding: 'bg-blue-500',
  compliance: 'bg-green-500',
  risk_assessment: 'bg-orange-500',
  esg: 'bg-emerald-500',
  financial: 'bg-yellow-500',
  quality: 'bg-purple-500',
  security: 'bg-red-500',
  capability: 'bg-cyan-500',
  periodic_review: 'bg-indigo-500',
  code_of_conduct: 'bg-pink-500',
  custom: 'bg-gray-500',
};

const datasetTypeIcons: Record<string, React.ElementType> = {
  onboarding: UserCheck,
  compliance: FileCheck,
  risk_assessment: BarChart3,
  esg: Leaf,
  financial: Building2,
  quality: CheckCircle2,
  security: Lock,
  capability: Briefcase,
  periodic_review: RefreshCw,
  code_of_conduct: Shield,
  custom: Settings,
};

export default function DatasetsList() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [datasets, setDatasets] = useState<OnboardingDataset[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<string>('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [datasetToDelete, setDatasetToDelete] = useState<OnboardingDataset | null>(null);

  useEffect(() => {
    fetchDatasets();
  }, []);

  const fetchDatasets = async () => {
    setLoading(true);
    try {
      const response = await onboardingAPI.getDatasets();
      setDatasets(response.data.results || response.data || []);
    } catch (error) {
      console.error('Failed to fetch datasets:', error);
      toast.error('Failed to load questionnaire templates');
    } finally {
      setLoading(false);
    }
  };

  const handleClone = async (dataset: OnboardingDataset) => {
    try {
      await onboardingAPI.cloneDataset(dataset.id);
      toast.success('Template cloned successfully');
      fetchDatasets();
    } catch (error) {
      toast.error('Failed to clone template');
    }
  };

  const handleDelete = async () => {
    if (!datasetToDelete) return;
    try {
      await onboardingAPI.deleteDataset(datasetToDelete.id);
      toast.success('Template deleted');
      setDeleteDialogOpen(false);
      setDatasetToDelete(null);
      fetchDatasets();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, 'Failed to delete template'));
    }
  };

  // Filter datasets based on tab and search
  const getFilteredDatasets = () => {
    let filtered = datasets;

    // Filter by tab
    if (activeTab === 'onboarding') {
      filtered = filtered.filter(d => d.dataset_type === 'onboarding');
    } else if (activeTab === 'questionnaire') {
      filtered = filtered.filter(d => d.dataset_type !== 'onboarding');
    }

    // Filter by search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(d =>
        d.name.toLowerCase().includes(query) ||
        d.description?.toLowerCase().includes(query)
      );
    }

    return filtered;
  };

  const filteredDatasets = getFilteredDatasets();

  // Count for tabs
  const onboardingCount = datasets.filter(d => d.dataset_type === 'onboarding').length;
  const questionnaireCount = datasets.filter(d => d.dataset_type !== 'onboarding').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const renderDatasetCard = (dataset: OnboardingDataset) => {
    const IconComponent = datasetTypeIcons[dataset.dataset_type] || ClipboardList;
    const bgColor = datasetTypeColors[dataset.dataset_type] || datasetTypeColors['custom'];

    return (
      <Card
        key={dataset.id}
        className="group relative hover:shadow-lg transition-all duration-200 cursor-pointer overflow-hidden"
        onClick={() => navigate(`/onboarding/datasets/${dataset.id}`)}
      >
        {/* Dropdown Menu */}
        <div className="absolute top-2 right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="icon" className="h-8 w-8 bg-white/80 hover:bg-white shadow-sm">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/onboarding/datasets/${dataset.id}`); }}>
                <Eye className="h-4 w-4 mr-2" />
                View
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/onboarding/datasets/${dataset.id}/edit`); }}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleClone(dataset); }}>
                <Copy className="h-4 w-4 mr-2" />
                Clone
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive"
                onClick={(e) => {
                  e.stopPropagation();
                  setDatasetToDelete(dataset);
                  setDeleteDialogOpen(true);
                }}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Status Badge */}
        {!dataset.is_active && (
          <div className="absolute top-2 left-2 z-10">
            <Badge variant="secondary" className="text-xs">Inactive</Badge>
          </div>
        )}

        <CardContent className="p-6 text-center">
          {/* Icon Container */}
          <div
            className={cn(
              "mx-auto w-32 h-32 rounded-xl flex items-center justify-center mb-4",
              "border-2 border-gray-100 shadow-sm",
              bgColor
            )}
          >
            <IconComponent className="h-16 w-16 text-white" />
          </div>

          {/* Dataset Name */}
          <h3 className="font-semibold text-lg text-gray-900 dark:text-gray-100 line-clamp-2 min-h-[3.5rem] mb-2">
            {dataset.name}
          </h3>

          {/* Type Badge */}
          <Badge variant="outline" className="mb-3">
            {datasetTypeLabels[dataset.dataset_type] || dataset.dataset_type}
          </Badge>

          {/* Stats */}
          <div className="flex justify-center gap-4 text-sm text-muted-foreground">
            <span>{dataset.sections_count || 0} sections</span>
            <span>•</span>
            <span>{dataset.questions_count || 0} questions</span>
          </div>

          {/* Version */}
          <div className="mt-2 text-xs text-muted-foreground">
            Version {dataset.version || 1}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/onboarding')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Questionnaire Templates</h1>
            <p className="text-muted-foreground">
              Create and manage questionnaire templates for supplier onboarding
            </p>
          </div>
        </div>
        <Button onClick={() => navigate('/onboarding/datasets/new')}>
          <Plus className="h-4 w-4 mr-2" />
          Create Template
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <TabsList className="bg-muted/50">
            <TabsTrigger value="all" className="gap-2">
              <ClipboardList className="h-4 w-4" />
              All Templates
              <Badge variant="secondary" className="ml-1">{datasets.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="questionnaire" className="gap-2">
              <FileText className="h-4 w-4" />
              Questionnaires
              <Badge variant="secondary" className="ml-1">{questionnaireCount}</Badge>
            </TabsTrigger>
            <TabsTrigger value="onboarding" className="gap-2">
              <UserCheck className="h-4 w-4" />
              Onboarding
              <Badge variant="secondary" className="ml-1">{onboardingCount}</Badge>
            </TabsTrigger>
          </TabsList>

          {/* Search */}
          <div className="relative w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {/* Content */}
        <TabsContent value={activeTab} className="mt-6">
          {filteredDatasets.length === 0 ? (
            <Card>
              <CardContent className="text-center py-16">
                <FileText className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-medium mb-2">No templates found</h3>
                <p className="text-muted-foreground mb-6">
                  {searchQuery
                    ? 'Try adjusting your search terms'
                    : 'Create your first questionnaire template to get started'}
                </p>
                {!searchQuery && (
                  <Button onClick={() => navigate('/onboarding/datasets/new')}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Template
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
              {filteredDatasets.map(renderDatasetCard)}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{datasetToDelete?.name}"? This action cannot be undone.
              Any supplier responses using this template will be preserved but the template will no longer be available for new assignments.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
